/**
 * WMS product/stock facade: delegates to picqer-stock / picqer-product-import or
 * channeldock-stock / channeldock-product-import based on wms_provider.
 * Used by My Products and product import.
 */
import { supabase } from '../lib/supabase';
import type { WmsProvider } from './wmsOrdersService';

export type { WmsProvider };

export interface WmsStockItem {
  productId: number;
  picqerProductId: number;
  stock: number;
  productName: string;
}

export interface WmsProductImportResult {
  picqerProducts: Record<string, unknown>[];
  matchedProducts: Array<{
    picqerProduct: Record<string, unknown>;
    existingProduct: Record<string, unknown>;
    matchType: 'ean' | 'productcode' | 'name';
  }>;
  newProducts: Record<string, unknown>[];
  totalFetched: number;
  filteredCount?: number;
  alreadyLinkedCount: number;
}

export const WmsProductService = {
  async getProductStock(
    companyId: number,
    wmsProvider: WmsProvider
  ): Promise<{ success: boolean; data?: WmsStockItem[]; error?: string }> {
    const fn = wmsProvider === 'channeldock' ? 'channeldock-stock' : 'picqer-stock';
    try {
      const { data, error } = await supabase.functions.invoke(fn, {
        body: { companyId },
      });
      if (error) throw error;
      const result = data as { success?: boolean; data?: WmsStockItem[]; error?: string };
      if (result?.success && Array.isArray(result.data)) {
        return { success: true, data: result.data };
      }
      return {
        success: false,
        error: (result?.error as string) ?? 'Failed to fetch product stock',
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to fetch product stock',
      };
    }
  },

  async importProducts(
    companyId: number,
    wmsProvider: WmsProvider,
    picqerApiKey?: string
  ): Promise<{ success: boolean; data?: WmsProductImportResult; error?: string }> {
    if (wmsProvider === 'channeldock') {
      try {
        const { data, error } = await supabase.functions.invoke('channeldock-product-import', {
          body: { companyId, action: 'import' },
        });
        if (error) throw error;
        const result = data as { success?: boolean; data?: WmsProductImportResult; error?: string };
        if (result?.success && result.data) {
          return { success: true, data: result.data };
        }
        return {
          success: false,
          error: (result?.error as string) ?? 'Failed to import products from ChannelDock',
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Failed to import products from ChannelDock',
        };
      }
    }
    if (!picqerApiKey) {
      return { success: false, error: 'Picqer API key is required for product import' };
    }
    try {
      const { data, error } = await supabase.functions.invoke('picqer-product-import', {
        body: { apiKey: picqerApiKey, companyId },
      });
      if (error) throw error;
      const result = data as { success?: boolean; data?: WmsProductImportResult; error?: string };
      if (result?.success && result.data) {
        return { success: true, data: result.data };
      }
      return {
        success: false,
        error: (result?.error as string) ?? 'Failed to import products from Picqer',
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to import products from Picqer',
      };
    }
  },

  async linkProduct(
    companyId: number,
    wmsProvider: WmsProvider,
    wmsProductId: number,
    existingProductId: number
  ): Promise<{ success: boolean; error?: string }> {
    const bulk = await this.linkProducts(companyId, wmsProvider, [
      { wmsProductId, existingProductId },
    ]);
    if (bulk.success && (bulk.errorCount ?? 0) === 0) return { success: true };
    return {
      success: false,
      error: bulk.errors?.[0] ?? bulk.error ?? 'Link failed',
    };
  },

  /** Link many catalog products to WMS product ids in one round-trip (ChannelDock) or sequential updates (Picqer). */
  async linkProducts(
    companyId: number,
    wmsProvider: WmsProvider,
    links: Array<{ wmsProductId: number; existingProductId: number }>
  ): Promise<{
    success: boolean;
    successCount: number;
    errorCount: number;
    errors?: string[];
    error?: string;
  }> {
    if (links.length === 0) {
      return { success: true, successCount: 0, errorCount: 0 };
    }
    if (wmsProvider === 'channeldock') {
      try {
        const { data, error } = await supabase.functions.invoke('channeldock-product-import', {
          body: {
            companyId,
            action: 'link',
            linkRequests: links.map((l) => ({
              existingProductId: l.existingProductId,
              picqerProductId: l.wmsProductId,
            })),
          },
        });
        if (error) throw error;
        const result = data as {
          success?: boolean;
          data?: { successCount: number; errorCount: number; errors?: string[] };
          error?: string;
        };
        if (!result?.success) {
          return {
            success: false,
            successCount: 0,
            errorCount: links.length,
            error: (result?.error as string) ?? 'Link failed',
          };
        }
        const d = result.data!;
        return {
          success: d.errorCount === 0,
          successCount: d.successCount,
          errorCount: d.errorCount,
          errors: d.errors,
        };
      } catch (err) {
        return {
          success: false,
          successCount: 0,
          errorCount: links.length,
          error: err instanceof Error ? err.message : 'Failed to link products',
        };
      }
    }
    let successCount = 0;
    const errors: string[] = [];
    for (const { wmsProductId, existingProductId } of links) {
      const { error } = await supabase
        .from('company_product')
        .update({ picqer_product_id: wmsProductId })
        .eq('id', existingProductId)
        .eq('company_id', companyId);
      if (error) {
        errors.push(`Product ${existingProductId}: ${error.message}`);
      } else {
        successCount++;
      }
    }
    const errorCount = links.length - successCount;
    return {
      success: errorCount === 0,
      successCount,
      errorCount,
      errors: errors.length ? errors : undefined,
    };
  },

  async unlinkProduct(
    companyId: number,
    wmsProvider: WmsProvider,
    productId: number
  ): Promise<{ success: boolean; error?: string }> {
    const column = wmsProvider === 'channeldock' ? 'channeldock_product_id' : 'picqer_product_id';
    const { error } = await supabase
      .from('company_product')
      .update({ [column]: null })
      .eq('id', productId)
      .eq('company_id', companyId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};
